// The query log (6.3): the bodies behind every query and AI answer. Access is
// enforced server-side: the Owner always; an Admin only under the Owner's grant,
// and the 403's explanation renders here so an ungranted Admin learns where the
// switch lives instead of meeting a dead end.
//
// Replay and diff were removed 2026-09-16 (list 7.5, the product owner: "what use
// is this? not a requested feature"). The log itself stays — 7.6 keeps the query
// and question text here rather than in the audit log, and 36.6 governs who reads it.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { listQueryLog, type QueryLogEntry } from '@/lib/api';
import { buttonVariants } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

const KIND_LABEL: Record<QueryLogEntry['kind'], string> = {
  query: 'Ran directly',
  'llm-query': 'Engine-authored',
  'llm-question': 'Question',
};

export function QueryLogTab() {
  const auth = useAuth();
  const token = auth.user?.id_token;
  const [answerFilter, setAnswerFilter] = useState('');

  const log = useQuery({
    queryKey: ['query-log', answerFilter],
    enabled: !!token,
    queryFn: async () => {
      const r = await listQueryLog(token!, answerFilter.trim() || undefined);
      if (!r.ok) throw new Error(r.message);
      return r.data.entries;
    },
  });

  if (log.isError) {
    return (
      <div className="mt-6 max-w-2xl rounded-md border border-border bg-card p-4 text-sm text-tres-text-primary">
        {String((log.error as Error).message)}
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-6">
      <p className="max-w-3xl text-sm text-muted-foreground">
        The queries behind every answer — what a person ran, and what the engine authored for
        the AI. The audit log records only that a query ran; the text lives here.
      </p>

      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-muted-foreground">
          Filter by answer id
          <input
            value={answerFilter}
            onChange={(e) => setAnswerFilter(e.target.value)}
            placeholder="urn:uuid:…"
            className="mt-1 block w-72 rounded border border-input bg-background px-2 py-1 font-mono text-xs"
          />
        </label>
      </div>

      {log.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading the log…</p>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border bg-card">
          {(log.data ?? []).map((e) => (
            <li key={e.id} className="space-y-1 p-3 text-xs">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded bg-tres-action/15 px-1.5 py-0.5 text-[10px] font-medium text-tres-action">
                  {KIND_LABEL[e.kind]}
                </span>
                <span className="text-muted-foreground">{e.ran_at}</span>
                <span className="text-muted-foreground">{e.actor}</span>
                {e.duration_ms != null && (
                  <span className="text-muted-foreground">{e.duration_ms} ms</span>
                )}
                {e.row_count != null && (
                  <span className="text-muted-foreground">{e.row_count} row(s)</span>
                )}
                {e.answer_id && (
                  <button
                    type="button"
                    onClick={() => setAnswerFilter(e.answer_id ?? '')}
                    className={cn(buttonVariants({ variant: 'link', size: 'sm' }), 'font-mono text-[10px]')}
                    title="Show every query behind this answer"
                  >
                    {e.answer_id}
                  </button>
                )}
              </div>
              <pre className="overflow-x-auto whitespace-pre-wrap break-all font-mono text-[11px] text-tres-text-primary">
                {e.body}
              </pre>
            </li>
          ))}
          {(log.data ?? []).length === 0 && (
            <li className="p-3 text-sm text-muted-foreground">
              Nothing logged yet — run a query or ask the assistant a question.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
